import axios from 'axios';

import { apiClient } from '@/services/api';
import { getAccessToken } from '@/utils/authTokens';
import { logger } from '@/utils/logger/loggerInstance';

// Hauser-EQ API configuration from environment variables
// VITE_HAUSER_EQ_BROWSER_URL is set in .env.local for local dev (Vite proxy path)
// Falls back to VITE_HAUSER_EQ_API_URL which SST sets from the HauserEqBaseUrl secret
const HAUSER_EQ_BASE_URL = (import.meta.env.VITE_HAUSER_EQ_BROWSER_URL ||
  import.meta.env.VITE_HAUSER_EQ_API_URL) as string;

export interface HauserEQCategoryScores {
  [category: string]: number | null;
}

export interface HauserEQIssueCountsByUrgency {
  Critical?: number;
  Urgent?: number;
  Moderate?: number;
  Low?: number;
  Monitor?: number;
}

export interface HauserEQConditionKPIs {
  repair_price_ratio?: number;
  total_repair_cost?: number;
  issue_counts_by_urgency?: HauserEQIssueCountsByUrgency;
  big_ticket_count?: number;
  big_ticket_percentage?: number;
}

export interface HauserEQPropertyDetails {
  price?: number;
  lot_size?: number;
  bedrooms?: number;
  bathrooms?: number;
  sqft?: number;
  year_built?: number;
  property_type?: string;
}

export interface HauserEQCategoryExplanations {
  [category: string]: string;
}

export interface HauserEQSubfactorExplanations {
  [category: string]: Record<string, string>;
}

export interface HauserEQPermitContractor {
  name?: string;
  organization?: string;
  phone?: string;
  address?: string;
  license_type?: string;
  license_number?: string;
  type?: string;
}

export interface HauserEQPermit {
  number?: string;
  status?: string;
  category?: string;
  type?: string;
  subtype?: string;
  classification?: string;
  work_description?: string;
  applied_date?: string;
  last_status_date?: string;
  expiration_date?: string;
  // Frequently null in practice — PropertyLens doesn't always populate this
  // even for finalized permits. Prefer `last_status_date` when displaying
  // a single date, since it's the more reliably-populated field.
  issued_date?: string;
  comments?: string;
  contractors?: HauserEQPermitContractor[];
}

export interface HauserEQSubfactorDetail {
  score: number | null;
  raw_score: number | null;
  display_name: string;
  details?: HauserEQNoiseExposureDetails
    | HauserEQAirQualityDetails
    | HauserEQWaterQualityDetails
    | HauserEQRadonMoldDetails
    | HauserEQCrimeOverallDetails
    | HauserEQCrimeComponentDetails
    | HauserEQLocationDetails
    | HauserEQAmenityDetails
    | HauserEQGenericSubfactorDetails;
}

export interface HauserEQAirQualityComponentDetail {
  rating?: string;
  grade?: string;
  description?: string;
  expected_range?: {
    min: number;
    max: number;
    unit: string;
  };
}

export interface HauserEQAirQualityDetails {
  overall?: HauserEQAirQualityComponentDetail & {
    national_percentile?: number;
  };
  components?: Partial<Record<'ozone' | 'fine_particulate', HauserEQAirQualityComponentDetail>>;
}

export interface HauserEQWaterQualityDetails {
  components: Partial<Record<
    'pfas' | 'hardness' | 'groundwater_arsenic',
    HauserEQAirQualityComponentDetail
  >>;
}

export interface HauserEQRadonMoldDetails {
  components: Partial<Record<'radon' | 'mold', HauserEQAirQualityComponentDetail>>;
}

export interface HauserEQCrimeOverallDetails {
  areas: Partial<Record<
    'immediate' | 'half_mile' | 'one_mile',
    HauserEQAirQualityComponentDetail
  >>;
}

export interface HauserEQCrimeComponentDetails {
  components: Partial<Record<
    | 'aggravated_assault'
    | 'robbery'
    | 'murder'
    | 'rape'
    | 'burglary'
    | 'larceny'
    | 'motor_vehicle_theft',
    HauserEQAirQualityComponentDetail
  >>;
}

export interface HauserEQMeasurement {
  value: number;
  unit?: string;
}

export interface HauserEQLocationServiceDetail {
  name?: string;
  type?: string;
  distance?: HauserEQMeasurement;
  driving_distance?: HauserEQMeasurement;
  driving_duration?: HauserEQMeasurement;
  volunteer?: boolean;
}

export interface HauserEQLocationDetails {
  rating?: string;
  grade?: string;
  description?: string;
  nearest_service?: HauserEQLocationServiceDetail;
}

export interface HauserEQAmenityNearestDetail {
  distance_miles: number;
  name?: string;
  type?: string;
  school_level?: string;
  student_teacher_ratio?: number;
  charter_school?: boolean;
  virtual_learning?: boolean;
  rating?: number;
  grade_span?: { lower?: string; upper?: string };
}

export interface HauserEQAmenitySourceDetail {
  label: string;
  total_count: number;
  coverage_radius_miles: number;
  count_within_radius: number;
  nearest?: HauserEQAmenityNearestDetail;
}

export interface HauserEQAmenityDetails {
  sources: Record<string, HauserEQAmenitySourceDetail>;
}

export interface HauserEQGenericSubfactorInput {
  key: string;
  path: string;
  value: string | number | boolean;
  value_type: 'value' | 'count';
  unit?: string;
  nearest_distance?: number;
  nearest_unit?: string;
  nearest_name?: string;
  national_percentile?: number;
  annualized_frequency?: number;
}

export interface HauserEQGenericSubfactorDetails {
  inputs: HauserEQGenericSubfactorInput[];
}

export interface HauserEQNoiseModeDetail {
  rating?: string;
  grade?: string;
  daily_equivalent_sound_level?: {
    value: number;
    unit?: string;
  };
}

export interface HauserEQNoiseExposureDetails {
  modes: Partial<Record<'aviation' | 'rail' | 'road', HauserEQNoiseModeDetail>>;
}

export interface HauserEQContextCategoryDetail {
  score: number;
  raw_score: number;
  subfactors: Record<string, HauserEQSubfactorDetail>;
  confidence: number;
}

export interface HauserEQScoreDriverEntry {
  driving_up: string[];
  driving_down: string[];
}

export interface HauserEQScoreDrivers {
  overall: HauserEQScoreDriverEntry;
  [key: string]: HauserEQScoreDriverEntry;
}

export interface HauserEQPOIItem {
  name: string;
  distance: { value: number; unit: string };
  // PropertyLens-sourced POIs use structured `coordinates`; Google Places
  // POIs use `location`. Consumers should handle both.
  coordinates?: { latitude: number; longitude: number };
  location?: { latitude: number; longitude: number };
  // PropertyLens returns a structured address; Google Places returns a single
  // formatted string. Both are exposed so consumers can render either.
  address?: {
    address?: string;
    city?: string;
    state?: string;
    zip_code?: string;
    formatted?: string;
  };
  type?: string;
  rating?: number;
  school_level?: string;
  crime_level?: string;
  // Google Places (New) fields. Present on items where `source === 'google_places'`.
  source?: 'google_places' | 'property_lens';
  category?: string;
  place_id?: string;
  business_status?: string;
  types?: string[];
}

export interface HauserEQNearestPOIs {
  // ─── PropertyLens-sourced ─────────────────────────────────────────────
  // Schools (real API uses public_schools/private_schools, fixture uses schools)
  schools?: HauserEQPOIItem[];
  public_schools?: HauserEQPOIItem[];
  private_schools?: HauserEQPOIItem[];
  colleges_and_universities?: HauserEQPOIItem[];
  childcare_centers?: HauserEQPOIItem[];
  // Medical
  hospitals?: HauserEQPOIItem[];
  hospitals_and_medical_centers?: HauserEQPOIItem[];
  // Emergency services
  fire_stations?: HauserEQPOIItem[];
  police_stations?: HauserEQPOIItem[];
  law_enforcement_facilities?: HauserEQPOIItem[];
  ambulance_services?: HauserEQPOIItem[];
  // Hazards
  superfund_sites?: HauserEQPOIItem[];
  brownfields?: HauserEQPOIItem[];
  toxic_release_facilities?: HauserEQPOIItem[];
  underground_storage_tanks?: HauserEQPOIItem[];
  asbestos_facilities?: HauserEQPOIItem[];
  // ─── Lifestyle (Google Places New) ────────────────────────────────────
  // Seeded once per property and cached for 365 days. Items carry
  // `source: 'google_places'` and `category` matching the key here.
  groceries?: HauserEQPOIItem[];
  pharmacies?: HauserEQPOIItem[];
  gas_stations?: HauserEQPOIItem[];
  cafes?: HauserEQPOIItem[];
  restaurants?: HauserEQPOIItem[];
  parks?: HauserEQPOIItem[];
  gyms?: HauserEQPOIItem[];
  libraries?: HauserEQPOIItem[];
  shopping_malls?: HauserEQPOIItem[];
  department_stores?: HauserEQPOIItem[];
  transit?: HauserEQPOIItem[];
  // Allow additional POI types from the API
  [key: string]: HauserEQPOIItem[] | undefined;
}

export interface HauserEQResults {
  property_key?: string;
  property_id?: string;
  // 'partial' means one of context/condition succeeded while the other is
  // still retrying via the DLQ reprocessor — see hauser-eq #17. Distinct
  // from 'failed', which the state machine no longer writes for a single
  // pipeline's failure (see #17) — that's carried by context_status/
  // condition_status below instead, per-pipeline.
  status: 'queued' | 'processing' | 'completed' | 'partial' | 'failed';
  condition_status?: 'pending' | 'completed' | 'no_list_price' | 'retrying' | 'failed';
  // context_status has no legacy 'pending'/'completed'/'no_list_price'
  // values the way condition_status does — before hauser-eq #17 wired
  // Step Functions into the DLQ infra, context-score had no distinct
  // in-between state to report at all.
  context_status?: 'retrying' | 'failed';
  // Populated once a pipeline's retries are exhausted for good (see
  // hauser-eq #16/#20's DLQ reprocessor give-up). Human-readable-ish JSON
  // blob, not a fixed schema — render defensively.
  context_error?: Record<string, unknown> | string;
  condition_error?: Record<string, unknown> | string;
  context_failed_at?: string;
  condition_failed_at?: string;
  created_at?: string;
  updated_at?: string;
  // Independent per-score timestamps — a new inspection report only
  // re-runs condition scoring, so `updated_at` alone can't tell a
  // consumer which score is actually fresh.
  condition_calculation_timestamp?: string;
  context_calculation_timestamp?: string;
  context_score?: number;
  condition_score?: number;
  context_category_scores?: HauserEQCategoryScores;
  condition_category_scores?: HauserEQCategoryScores;
  context_subscores?: HauserEQCategoryScores;
  condition_subscores?: HauserEQCategoryScores;
  condition_kpis?: HauserEQConditionKPIs;
  property_details?: HauserEQPropertyDetails;
  // LLM-generated explanations
  context_score_explanation?: string;
  condition_score_explanation?: string;
  context_score_subscore_explanations?: HauserEQCategoryExplanations;
  condition_score_subscore_explanations?: HauserEQCategoryExplanations;
  context_score_category_explanations?: HauserEQCategoryExplanations;
  context_score_subfactor_explanations?: HauserEQSubfactorExplanations;
  condition_score_category_explanations?: HauserEQCategoryExplanations;
  context_category_details?: Record<string, HauserEQContextCategoryDetail>;
  condition_score_score_drivers?: HauserEQScoreDrivers;
  context_score_score_drivers?: HauserEQScoreDrivers;
  nearest_points_of_interest?: HauserEQNearestPOIs;
  permits?: HauserEQPermit[];
}

// Create a separate axios client for Hauser-EQ API
const hauserEqClient = axios.create({
  baseURL: HAUSER_EQ_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

// Add request interceptor to include auth headers
hauserEqClient.interceptors.request.use((config) => {
  // Add user's JWT token for authentication
  const accessToken = getAccessToken();
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }

  return config;
});

/**
 * Get Hauser-EQ evaluation results for a property by propertyId.
 * Calls GET /evaluations/{propertyId} on the HauserEQ API.
 */
export const getHauserEQResultsForProperty = async (
  propertyId: string,
): Promise<HauserEQResults> => {
  const url = `/evaluations/${propertyId}`;
  logger.debug(`GET Hauser-EQ ${url}`);

  const response = await hauserEqClient.get<HauserEQResults>(url);
  return response.data;
};

/**
 * Check if Hauser-EQ API is configured.
 * Returns false if environment variables are not set.
 */
export const isHauserEQConfigured = (): boolean => Boolean(HAUSER_EQ_BASE_URL);

/**
 * Manually retry a HauserEQ pipeline that's landed in a failed state.
 * Goes through blueprint-mono's own backend (POST /properties/:id/hauser-eq/retry),
 * not hauser-eq directly — that route holds the server-side recalculate API
 * key, which must never reach the browser. See #19.
 */
export const retryHauserEQEvaluation = async (
  propertyId: string,
  scoreType: 'area' | 'home',
): Promise<void> => {
  const url = `/properties/${propertyId}/hauser-eq/retry`;
  logger.debug(`POST ${url}`, { scoreType });
  await apiClient.post(url, { scoreType });
};
